import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { SupplierDetails } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

type SupplierDetailsQuery = {
  suppliers: {
    results: SupplierDetails[];
    totalResults: number;
  };
};

const SUPPLIER_DETAILS_QUERY = graphql(`
  query supplierDetails($guids: [String!]) {
    suppliers(SuppliersRequest: { guids: $guids }) {
      results {
        id
        guid
        name
        country
        countryCode
        isSme
        address
        town
        postalCode
        phone
        email
        isStotlesVerified
        isArchived
        createdAt
        updatedAt
        referenceOrganisations {
          name
          source
          sourceIdentifier
          address {
            address
            address2
            postCode
            countryCode
          }
          basicContactInfo {
            phone
            websiteUrl
          }
        }
      }
      totalResults
    }
  }
`);

export type SupplierFromQuery = SupplierDetailsQuery["suppliers"]["results"][number];

/**
 * Returns detailed information about a supplier
 * @param supplierGuid The GUID of the supplier to fetch
 * @param options Optional React Query options
 */
export function useSupplierDetailsGql(
  supplierGuid: string,
  options?: UseQueryOptions<SupplierDetailsQuery, unknown, SupplierDetailsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["supplierDetails", supplierGuid],
    SUPPLIER_DETAILS_QUERY,
    [{ guids: [supplierGuid] }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return {
    data: data?.suppliers.results,
    ...rest,
  };
}
